// Runtime event folding. Wire types and pure activity presentation are
// separate so replay and live frames share one source of truth.
import { mergeBackgroundWork } from '@/frontend/features/AgentChats/events/backgroundWork'
import { currentStreamingText, upsertTextSegment } from '@/frontend/features/AgentChats/events/textStreaming'
import type { AgentEvent, ApprovalDecision, ForgeAnalyticsPresentation, ProcedurePresentation, RunActivity, ToolCallActivity } from '@/frontend/features/AgentChats/events/agentEventTypes'
import type { HtmlArtifact } from '@/frontend/features/AgentChats/artifacts/types'
import type { ForgeDashboardArtifact } from '@/frontend/features/AgentChats/events/chatArtifacts'

export * from '@/frontend/features/AgentChats/events/agentEventTypes'
export { activityHeadline, activityStatusLabel, groupIntoPhases, groupToolCalls, hasFailure, hasToolWarnings, settlePersistedActivity } from '@/frontend/features/AgentChats/events/agentActivityPresentation'
export type { AgentChatArtifact, ForgeDashboardArtifact } from '@/frontend/features/AgentChats/events/chatArtifacts'
export type { HtmlArtifact } from '@/frontend/features/AgentChats/artifacts/types'
export type { BackgroundWorkKind, BackgroundWorkStatus, BackgroundWorkPresentation } from '@/frontend/features/AgentChats/events/backgroundWork'
export { currentCommentaryText, currentStreamingText, stripWorkstationFileEnvelope } from '@/frontend/features/AgentChats/events/textStreaming'
export type { TextStreamSegment } from '@/frontend/features/AgentChats/events/textStreaming'

export const EMPTY_RUN_ACTIVITY: RunActivity = {
  runId: '',
  turnId: '',
  status: 'queued',
  summary: '',
  completedToolCount: 0,
  activeToolCount: 0,
  toolCalls: [],
  requiresUserAction: false,
  approval: null,
  plan: [],
  planNote: '',
  text: '',
  textSegments: [],
  startedAt: null,
  finishedAt: null,
  contextUsage: null,
}

function upsertToolCall(
  toolCalls: ToolCallActivity[],
  toolCallId: string,
  patch: Partial<ToolCallActivity>,
): ToolCallActivity[] {
  const definedPatch = Object.fromEntries(
    Object.entries(patch).filter(([, value]) => value !== undefined),
  ) as Partial<ToolCallActivity>
  const index = toolCalls.findIndex((call) => call.toolCallId === toolCallId)
  if (index === -1) {
    return [
      ...toolCalls,
      { toolCallId, activityKey: 'agent_action', summary: '', status: 'running', ...definedPatch },
    ]
  }
  return toolCalls.map((call, position) =>
    position === index ? { ...call, ...definedPatch } : call,
  )
}

function procedureHasFailed(procedure?: ProcedurePresentation): boolean {
  return ['failed', 'error', 'denied', 'needs_authorization'].includes(procedure?.status || '')
}

function mergeProcedurePresentation(
  current?: ProcedurePresentation,
  next?: ProcedurePresentation,
): ProcedurePresentation | undefined {
  if (!next) return current
  if (!current) return next

  const merged: ProcedurePresentation = {
    ...current,
    ...next,
    title: next.title || current.title,
    draft: next.draft || current.draft,
    review: next.review || current.review,
    approval: next.approval || current.approval,
    process: next.process || current.process,
  }

  // Tool Catalog emits a concrete result and then transport wrappers with the
  // same call id. Those wrappers may contain only the generic start payload.
  // They must never erase a durable draft or move a completed operation back
  // to "running"; a genuinely new revision receives a new tool call id.
  if (current.status !== 'running' && next.status === 'running') {
    merged.status = current.status
    merged.progress = current.progress
    merged.replyText = current.replyText
  }

  return merged
}

function counts(toolCalls: ToolCallActivity[]) {
  return {
    completedToolCount: toolCalls.filter((call) => call.status === 'completed').length,
    activeToolCount: toolCalls.filter((call) => call.status === 'running').length,
  }
}

// Folds one event into the run view model. Pure so replayed events and live
// events produce the same result — reconnect must not change what is shown.
export function reduceAgentEvent(state: RunActivity, event: AgentEvent): RunActivity {
  const { payload } = event
  const eventAt = event.occurred_at || null
  const base = {
    ...state,
    turnId: event.turn_id,
    runId: event.run_id || state.runId,
    startedAt: state.startedAt || eventAt,
  }

  switch (event.event_type) {
    case 'RUN_STARTED':
      return { ...base, status: 'running' }

    case 'TEXT_MESSAGE_CONTENT': {
      const textSegments = upsertTextSegment(base.textSegments, event)
      return {
        ...base,
        status: 'running',
        textSegments,
        text: currentStreamingText(textSegments),
      }
    }

    case 'TEXT_MESSAGE_END':
      return { ...base, status: 'completed', finishedAt: eventAt }

    case 'TOOL_CALL_START': {
      const existing = base.toolCalls.find((call) => call.toolCallId === payload.toolCallId)
      const toolCalls = upsertToolCall(base.toolCalls, payload.toolCallId || '', {
        activityKey: payload.activityKey || existing?.activityKey || 'agent_action',
        summary: payload.summary || existing?.summary || '',
        completedSummary: payload.completedSummary || existing?.completedSummary,
        status: 'running',
        phase: payload.phase || existing?.phase,
        phaseLabel: payload.phaseLabel || existing?.phaseLabel,
        sourceLabel: payload.sourceLabel || existing?.sourceLabel,
        args: existing?.args || payload.args,
        procedure: mergeProcedurePresentation(existing?.procedure, payload.procedure),
        backgroundWork: mergeBackgroundWork(existing?.backgroundWork, payload.backgroundWork),
      })
      return {
        ...base,
        status: 'running',
        // Keep the agent's concrete progress narration in the turn when a
        // tool starts. It is real output the user already read, and removing
        // it makes the conversation appear to rewrite itself mid-execution.
        // Durable final content is still sourced from Fleet's final assistant
        // message, so this does not restore the old concatenated final payload.
        summary: payload.summary || base.summary,
        toolCalls,
        ...counts(toolCalls),
      }
    }

    case 'TOOL_CALL_RESULT': {
      const existing = base.toolCalls.find((call) => call.toolCallId === payload.toolCallId)
      const procedure = mergeProcedurePresentation(existing?.procedure, payload.procedure)
      const procedureFailed = procedureHasFailed(procedure)
      const toolCalls = upsertToolCall(base.toolCalls, payload.toolCallId || '', {
        activityKey: payload.activityKey || existing?.activityKey || 'agent_action',
        summary: existing?.completedSummary || payload.summary || existing?.summary || '',
        status: procedureFailed ? 'failed' : 'completed',
        sourceLabel: payload.sourceLabel || existing?.sourceLabel,
        resultPreview: payload.resultPreview,
        analytics: payload.analytics || existing?.analytics,
        artifact: payload.artifact || existing?.artifact,
        error: procedureFailed ? procedure?.progress?.message : undefined,
        procedure,
        backgroundWork: mergeBackgroundWork(existing?.backgroundWork, payload.backgroundWork),
      })
      return {
        ...base,
        summary: payload.summary || base.summary,
        toolCalls,
        ...counts(toolCalls),
      }
    }

    case 'TOOL_CALL_ERROR': {
      const existing = base.toolCalls.find((call) => call.toolCallId === payload.toolCallId)
      const toolCalls = upsertToolCall(base.toolCalls, payload.toolCallId || '', {
        activityKey: existing?.activityKey || payload.activityKey || 'agent_action',
        summary: existing?.completedSummary || payload.summary || existing?.summary || '',
        status: 'failed',
        sourceLabel: existing?.sourceLabel || payload.sourceLabel,
        error: payload.error,
        procedure: mergeProcedurePresentation(existing?.procedure, payload.procedure),
        backgroundWork: mergeBackgroundWork(existing?.backgroundWork, payload.backgroundWork),
      })
      return { ...base, toolCalls, ...counts(toolCalls) }
    }

    case 'PLAN_UPDATED':
      // Replaces rather than merges: the tool always publishes the whole plan,
      // and merging would resurrect steps the agent deliberately dropped.
      return {
        ...base,
        status: base.status === 'queued' ? 'running' : base.status,
        plan: payload.plan ?? base.plan,
        planNote: payload.explanation ?? base.planNote,
      }

    case 'CONTEXT_USAGE_UPDATED':
      return {
        ...base,
        contextUsage: {
          modelContextWindow: payload.modelContextWindow ?? null,
          contextTokens: payload.contextTokens ?? payload.lastTurnTokens ?? 0,
          totalTokens: payload.totalTokens ?? 0,
          inputTokens: payload.inputTokens ?? 0,
          cachedInputTokens: payload.cachedInputTokens ?? 0,
          outputTokens: payload.outputTokens ?? 0,
          reasoningOutputTokens: payload.reasoningOutputTokens ?? 0,
          lastTurnTokens: payload.lastTurnTokens ?? 0,
        },
      }

    case 'APPROVAL_REQUIRED':
      return {
        ...base,
        status: 'waiting_for_user',
        summary: payload.summary || base.summary,
        requiresUserAction: true,
        approval: {
          id: payload.approvalId,
          kind: payload.approvalKind,
          summary: payload.summary || 'Aguardando sua aprovação',
          detail: payload.detail,
          status: 'pending',
          allowedDecisions: payload.allowedDecisions ?? [],
          expiresAtMs: payload.expiresAtMs,
        },
      }

    case 'INTEGRATION_AUTH_REQUIRED':
      return {
        ...base,
        status: 'waiting_for_user',
        summary: payload.summary || base.summary,
        requiresUserAction: true,
      }

    case 'APPROVAL_RESOLVED':
      // Approval frames can be redelivered or arrive out of order. Never let a
      // stale resolution for A clear a newer pending approval B.
      if (!payload.approvalId || !base.approval?.id || payload.approvalId !== base.approval.id) {
        return base
      }

      return {
        ...base,
        status: 'running',
        requiresUserAction: false,
        approval: {
          ...base.approval,
          status: 'resolved',
          decision: isApprovalDecision(payload.decision) ? payload.decision : undefined,
        },
      }

    case 'RUN_FINISHED':
      return {
        ...base,
        status: 'completed',
        requiresUserAction: false,
        approval: base.approval?.status === 'pending' ? null : base.approval,
        finishedAt: eventAt,
      }

    case 'RUN_ERROR':
      return {
        ...base,
        status: 'failed',
        requiresUserAction: false,
        approval: base.approval?.status === 'pending' ? null : base.approval,
        finishedAt: eventAt,
      }

    case 'RUN_CANCELLED':
      return {
        ...base,
        status: 'cancelled',
        requiresUserAction: false,
        approval: base.approval?.status === 'pending' ? null : base.approval,
        finishedAt: eventAt,
      }

    default:
      return base
  }
}

function isApprovalDecision(value: string | undefined): value is ApprovalDecision {
  return value === 'allow-once' || value === 'allow-always' || value === 'deny'
}

export function reduceAgentEvents(events: AgentEvent[]): RunActivity | null {
  if (events.length === 0) return null

  const ordered = [...events].sort((left, right) => {
    const leftOccurredAt = Date.parse(left.occurred_at || '')
    const rightOccurredAt = Date.parse(right.occurred_at || '')
    if (Number.isFinite(leftOccurredAt) && Number.isFinite(rightOccurredAt) && leftOccurredAt !== rightOccurredAt) {
      return leftOccurredAt - rightOccurredAt
    }
    if (left.seq === right.seq) return 0
    if (left.seq === null) return 1
    if (right.seq === null) return -1
    return left.seq - right.seq
  })
  const turnId = ordered[0]?.turn_id || ''

  return ordered.reduce(
    (state, event) => reduceAgentEvent(state, event),
    { ...EMPTY_RUN_ACTIVITY, turnId },
  )
}

export function latestForgeAnalytics(
  activity: RunActivity | null,
): ForgeAnalyticsPresentation | null {
  if (!activity) return null

  return [...activity.toolCalls]
    .reverse()
    .find((toolCall) => toolCall.analytics)
    ?.analytics ?? null
}

export function latestForgeDashboardArtifact(
  activity: RunActivity | null,
): ForgeDashboardArtifact | null {
  if (!activity) return null

  const artifact = [...activity.toolCalls]
    .reverse()
    .find((toolCall) => toolCall.artifact && toolCall.artifact.kind !== 'desktop_handoff')
    ?.artifact

  return artifact?.kind === 'forge_dashboard' ? artifact : null
}

export function latestHtmlArtifact(
  activity: RunActivity | null,
): HtmlArtifact | null {
  if (!activity) return null

  const artifact = [...activity.toolCalls]
    .reverse()
    .find((toolCall) => toolCall.artifact && toolCall.artifact.kind !== 'desktop_handoff')
    ?.artifact

  return artifact?.kind === 'html_artifact' ? artifact : null
}

export function latestProcedurePresentation(
  activity: RunActivity | null,
): ProcedurePresentation | null {
  if (!activity) return null

  const presentations = activity.toolCalls
    .map((toolCall) => toolCall.procedure)
    .filter((procedure): procedure is ProcedurePresentation => Boolean(procedure))
  const last = presentations.at(-1)
  const latest = activity.status === 'completed'
    ? [...presentations].reverse().find((procedure) => procedure.status !== 'running') || last
    : last
  if (!latest) return null

  const durable = [...presentations].reverse().find((procedure) => procedure.draft)
  if (!durable || latest.draft) return latest

  // Tool Catalog emits a concrete Process call and one or more transport
  // wrappers. A later wrapper can carry only status/progress; retain the last
  // structured snapshot so the live canvas never collapses back to empty.
  return {
    ...durable,
    ...latest,
    title: latest.title || durable.title,
    draft: durable.draft,
    review: latest.review || durable.review,
    process: latest.process || durable.process,
  }
}
